import {
    ArrayUtil, DynamicLoader, ErrorMessage, getThemeColor, getThemeForKey, HorizontalAlignment, Logger, LogManager,
    ServerError
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, ButtonProps, ComponentCreator, Dialog, DialogProps, ComponentFactory, Label, Panel, PanelProps, StackDialog,
    Textbox, TextboxProps
} from "@mcleod/components";

export interface YesNoDialogProps extends DialogProps {
    defaultButton?: "yes" | "no",
    yesButtonProps?: Partial<ButtonProps>;
    noButtonProps?: Partial<ButtonProps>;
    yesButtonCaption?: string;
    noButtonCaption?: string;
}

export enum DiscardSaveCancelChoice {
    DISCARD = "discard",
    SAVE = "save",
    CANCEL = "cancel"
}

export class CommonDialogs {
    private static _errorHeaderThemeObject: any;
    private static _log: Logger;

    static get errorHeaderThemeObject(): any {
        if (this._errorHeaderThemeObject == null) {
            this._errorHeaderThemeObject = getThemeForKey("dialog.errorHeader") ?? {};
        }
        return this._errorHeaderThemeObject;
    }

    public static createDialog(components: ComponentCreator | ComponentCreator[], dialogProps: Partial<DialogProps>): Dialog {
        const array: ComponentCreator[] = ArrayUtil.getAsArray(components);
        const realComponents = ComponentFactory.createMultipleCommon(array, { color: getThemeColor("defaultColor") });
        const dialog = new Dialog(dialogProps);
        dialog.add(...realComponents);
        return dialog;
    }

    public static async showDialog(components: ComponentCreator | ComponentCreator[], dialogProps?: Partial<DialogProps>): Promise<Dialog> {
        const dialog = CommonDialogs.createDialog(components, dialogProps);
        await dialog.show();
        return Promise.resolve(dialog);
    }

    /**
     * This function shows an error dialog and allows the user to email support with the error details.
     *
     * @param {string} error
     * @param {string} title
     */
    public static async showError(error: ComponentCreator | ServerError | Error | ErrorMessage, title: string = "Error"): Promise<Dialog> {
        if (error == null)
            error = "An exception with no detail was thrown.";
        if (error instanceof ServerError)
            return CommonDialogs.showServerError(error, title === "Error" ? undefined : title);
        else if (error instanceof Error)
            return StackDialog.showStackDialog(error, { title: title });
        else if (typeof error === "object") {
            if ("displayed" in error && error.displayed === true) // the ErrorMessage interface from ErrorHandler
                return Promise.resolve(null);
        }
        return CommonDialogs.showMessage(error.toString(), title,
            { panelTitleProps: { ...CommonDialogs.errorHeaderThemeObject }});
    }

    /**
     * This displays a dialog that prompts the user to select 'Yes' or 'No'.  This function returns a
     * Promise whose result is a boolean value.  A true value in that result indicates the user selected 'Yes'.
     * A false value in that result indicates the user selected 'No'.  An undefined value in that result indicates
     * the user cancelled, either by clicking the X button or by using the Escape key.
     * @param content
     * @param title
     * @param props
     * @returns
     */
    public static showYesNo(content: ComponentCreator, title?: string, props?: Partial<YesNoDialogProps>, showIcons?: boolean, buttonWidth?: number): Promise<boolean> {
        const panel = new Panel({ fillRow: true });
        const component = ComponentFactory.createCommon(content);
        panel.add(component);
        const panelYesNo = new Panel({ fillRow: true, align: HorizontalAlignment.CENTER, marginTop: 24 });
        const buttonNo = new Button({
            caption: props?.noButtonCaption ?? "No", color: props?.defaultButton === "no" ? "primary.reverse" : "subtle.darker", backgroundColor: props?.defaultButton === "no" ? "primary" : "", imageName: ImageName.close, rowBreak: false, marginRight: 48, ...props?.noButtonProps
        });
        buttonNo.addClickListener(() => dlg.close(false));
        const buttonYes = new Button({ caption: props?.yesButtonCaption ?? "Yes", color: props?.defaultButton === "no" ? "subtle.darker" : "primary.reverse", backgroundColor: props?.defaultButton === "no" ? "" : "primary", imageName: ImageName.check, default: true, ...props?.yesButtonProps });
        buttonYes.addClickListener(() => {
            if (dlg.validateSimple())
                dlg.close(true);
        });
        if (showIcons != null && !showIcons) {
            buttonNo.imageName = null;
            buttonYes.imageName = null;
        }
        if (buttonWidth != null) {
            buttonNo.width = buttonWidth;
            buttonYes.width = buttonWidth;
        }
        panelYesNo.add(buttonNo);
        panelYesNo.add(buttonYes);
        panel.add(panelYesNo);
        props = props ?? {};
        const dlg = CommonDialogs.createDialog(panel, { title: title, okVisible: false, ...props });
        return dlg.show();
    }

    public static showDestructive(content: ComponentCreator, title?: string, props?: Partial<YesNoDialogProps>, showIcons?: boolean, buttonWidth?: number): Promise<boolean> {
        const mergedProps = {
            ...props,
            yesButtonProps: {
                backgroundColor: "error", color: "error.reverse", ...props?.yesButtonProps
            }
        }
        return CommonDialogs.showYesNo(content, title, mergedProps, showIcons, buttonWidth);
    }

    /**
     * This displays a dialog that prompts the user to select 'Discard Changes', 'Save Changes', or 'Cancel'.  This function returns a
     * Promise whose result is a boolean value.  A true in that result indicates the user selected 'Save Changes'.
     * false is returned if the user clicked 'Discard Changes', 'Cancel', or somehow otherwise closed the dialog.
     * @param props
     * @returns
     */
    public static async showDiscardSaveCancelDialog(props?: Partial<PanelProps>): Promise<DiscardSaveCancelChoice> {
        const panel = new Panel({ fillRow: true, padding: 0, ...props });
        panel.add(new Label({ text: "You have unsaved data. Do you want to:", rowBreak: true }));
        const discardButton = new Button({ caption: "Discard Changes", rowBreak: false, color: "subtle.darker" });
        discardButton.addClickListener(event => dialog.close(DiscardSaveCancelChoice.DISCARD));
        panel.add(discardButton);
        const saveButton = new Button({ caption: "Save Changes", rowBreak: false, color: "subtle.darker" });
        saveButton.addClickListener(event => dialog.close(DiscardSaveCancelChoice.SAVE));
        panel.add(saveButton);
        const cancelButton = new Button({ caption: "Cancel", rowBreak: false, default: true, color: "primary" });
        cancelButton.addClickListener(event => dialog.close(DiscardSaveCancelChoice.CANCEL));
        panel.add(cancelButton);
        const dialog = CommonDialogs.createDialog(panel, { title: "Are you sure?", okVisible: false });
        return dialog.show();
    }

    /**
     * Shows a dialog with a message and a simple text box for the user to enter a value.
     *
     * @param {ComponentCreator} content The message to display to the user.
     * @param {string} title The optional title of the dialog.  If not provided, it will simply display the application name (usually something like "LoadMaster - ABC Trucking")
     * @param {object} textboxProps An optional set of properties to pass to the Textbox that is shown on the Dialog
     * @param {object} dialogProps An optional set of properties to pass to the dialog that is shown.
     * @returns
     */
    public static async showInputDialog(content: ComponentCreator, title?: string, textboxProps?: Partial<TextboxProps>, dialogProps?: Partial<DialogProps>): Promise<string> {
        const panel = new Panel({ fillRow: true }); // do we need an argument to affect the props of this panel?
        const comp = ComponentFactory.createCommon(content);
        const input = new Textbox({ captionVisible: false, fillRow: true, required: true, ...textboxProps });
        panel.add(comp);
        panel.add(input);
        let returnValue = undefined;
        const dlg = CommonDialogs.createDialog(panel, { title: title, okVisible: true, ...dialogProps });
        const response = await dlg.show();
        if (response != undefined) {
            returnValue = input.selectedItem?.value || input.text;
        }
        return Promise.resolve(returnValue);
    }

    /**
     * Shows a simple text message to the user in a modal window.
     *
     * @param {string} content The message to display to the user.
     * @param {string} title The optional title of the dialog.  If not provided, it will simply display the application name (usually something like "LoadMaster - ABC Trucking")
     * @param {object} dialogProps An optional set of properties to pass to the dialog that is shown.
    */
    public static async showMessage(content: ComponentCreator, title?: string, dialogProps?: Partial<DialogProps>): Promise<Dialog> {
        const comp = ComponentFactory.createCommon(content);
        return CommonDialogs.showDialog(comp, { title: title, ...dialogProps });
    }

    /**
     * This is a convenience function to display a very generic error message when we fail to process the response from an API call.  It will also log the error behind the scenes so we can diagnose it.
     * This differs from showServerError in that this should be displayed if the API call succeeds but we have a problem reading or handling the response.
     *
     * @param {object} response
     * @param {string} error
     */
    public static async showUnknownResponseError(response: any, error: string): Promise<Dialog> {
        CommonDialogs.log.info("Unknown response %o     Error %o", response, error);
        return CommonDialogs.showError("There was an error handling the response from the server.");
    }

    /**
     * This displays a ServerError (created by exceptions in Api calls) in a modal dialog box.
     *
     * @param {ServerError} error
     * @param {string} title
     */
    public static async showServerError(error: ServerError, title: string = "Server Error"): Promise<Dialog> {
        CommonDialogs.log.info("Server error %o", error);
        const module = DynamicLoader.getModuleByName("common/ServerErrorPanel");
        const panel = new module.ServerErrorPanel(error, { margin: 0, padding: 0 });
        const props = {
            panelTitleProps: { ...CommonDialogs.errorHeaderThemeObject },
            title
        };
        return CommonDialogs.showDialog(panel, props);
    }

    public static async showDataConflictError(error: ServerError): Promise<boolean> {
        const module = DynamicLoader.getModuleByName("common/DataConflictErrorPanel");
        const panel = new module.DataConflictErrorPanel(error, { margin: 0, padding: 0});
        return CommonDialogs.showYesNo(panel,
            null,
            {
                resizable: true,
                titleProps: { text: "Record Updated by Another User", imageName: ImageName.warning},
                yesButtonProps: { caption: "Refresh", imageName: ImageName.arrowRefresh, width: 100},
                noButtonProps: { caption: "Cancel", imageName: null, width: 100 }
            });
    }

    private static get log(): Logger {
        if (CommonDialogs._log == null)
            CommonDialogs._log = LogManager.getLogger("common.CommonDialogs");
        return CommonDialogs._log;
    }
}
